<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDateTime, nowApiDateTime } from '@yuksalish/ui'
import { useOperatorApi } from '../composables/useOperatorApi'
import type {
  DowntimeEvent,
  DowntimeReason,
  MachineAssignment,
  MachineFault,
  MachineStatus,
  MaintenanceRequest,
  MaterialRequirement,
  OperatorOrder,
  OperatorStage,
  SetupParameter,
} from '../types/operator'

const route = useRoute()
const { t } = useI18n()
const {
  listMachines,
  listAssignments,
  getOrder,
  listStages,
  listDowntimeReasons,
  openDowntime,
  closeDowntime,
  listDowntime,
  getMachineSetup,
  saveMachineSetup,
  getMachineRequirements,
  listMachineFaults,
  reportMachineFault,
  resolveMachineFault,
  listMachineMaintenance,
  requestMachineMaintenance,
  updateMaintenanceStatus,
  recordMachineCounter,
} = useOperatorApi()

const machineId = computed(() => Number(route.params.id))

const machine = ref<MachineStatus | null>(null)
const order = ref<OperatorOrder | null>(null)
const stages = ref<OperatorStage[]>([])
const assignments = ref<MachineAssignment[]>([])
const downtimeEvents = ref<DowntimeEvent[]>([])
const downtimeReasons = ref<DowntimeReason[]>([])
const setupParameters = ref<SetupParameter[]>([])
const requirements = ref<MaterialRequirement[]>([])
const faults = ref<MachineFault[]>([])
const maintenanceRequests = ref<MaintenanceRequest[]>([])
const requirementsLoading = ref(false)
const requirementsError = ref('')

const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const submitting = ref(false)

const showDowntime = ref(false)
const downtimeForm = reactive({ reason_code: '', notes: '' })

const showSetup = ref(false)
const setupForm = reactive<{ code: string; value: string }[]>([
  { code: 'needle_count', value: '' },
  { code: 'gauge', value: '' },
  { code: 'tension', value: '' },
  { code: 'yarn_feed', value: '' },
])
const setupNotes = ref('')

const showFault = ref(false)
const faultForm = reactive({ fault_code: '', description: '' })

const showMaintenance = ref(false)
const maintenanceForm = reactive({ reason: '', priority: 'medium', notes: '' })

const showCounter = ref(false)
const counterForm = reactive({ counter_total: 0, counter_shift: 0 })

const activeAssignments = computed(() => assignments.value.filter((assignment) => assignment.status === 'active'))
const activeAssignment = computed(() => activeAssignments.value[0] ?? null)
const assignedStage = computed(() => {
  const stageCode = activeAssignment.value?.stage_code
  return stageCode ? stages.value.find((stage) => stage.stage_code === stageCode) ?? null : null
})
const currentStage = computed(() => assignedStage.value ?? stages.value.find((stage) => stage.status !== 'completed') ?? stages.value[0] ?? null)
const plannedQuantity = computed(() => Number(order.value?.planned_quantity ?? currentStage.value?.planned_quantity ?? 0))
const producedQuantity = computed(() => Number(order.value?.produced_quantity ?? currentStage.value?.completed_quantity ?? 0))
const remainingQuantity = computed(() => Math.max(plannedQuantity.value - producedQuantity.value, 0))
const completionPercent = computed(() => {
  if (plannedQuantity.value <= 0) return 0
  return Math.min(100, Math.max(0, Math.round((producedQuantity.value / plannedQuantity.value) * 100)))
})
const assignedLot = computed(() => machine.value?.current_lot_number ?? activeAssignment.value?.lot_number ?? '')
const requiredSetupCodes = ['needle_count', 'gauge', 'tension', 'yarn_feed']
const setupByCode = computed(() => new Map(setupParameters.value.map((parameter) => [parameter.code, parameter])))
const completedSetupCount = computed(() => requiredSetupCodes.filter((code) => setupByCode.value.get(code)?.value).length)
const setupComplete = computed(() => completedSetupCount.value === requiredSetupCodes.length)
const currentStageRequirements = computed(() => {
  const stageCode = currentStage.value?.stage_code
  return stageCode ? requirements.value.filter((requirement) => requirement.stage_code === stageCode) : requirements.value
})
const materialTotals = computed(() => {
  const totals = new Map<string, number>()
  requirements.value.forEach((requirement) => {
    const unit = requirement.unit || ''
    totals.set(unit, (totals.get(unit) ?? 0) + Number(requirement.required_quantity || 0))
  })
  return [...totals.entries()].map(([unit, quantity]) => ({ unit, quantity }))
})
const plannedMaterialPairs = computed(() => {
  if (!requirements.value.length) return 0
  return Math.max(...requirements.value.map((requirement) => Number(requirement.planned_pairs || 0)), 0)
})
const openDowntimeEvents = computed(() => downtimeEvents.value.filter((event) => !event.end_time))
const closedDowntimeEvents = computed(() => downtimeEvents.value.filter((event) => event.end_time))
const openFaults = computed(() => faults.value.filter((fault) => fault.status === 'open'))
const resolvedFaults = computed(() => faults.value.filter((fault) => fault.status === 'resolved'))
const openMaintenanceRequests = computed(() => maintenanceRequests.value.filter((request) => request.status !== 'done'))
const doneMaintenanceRequests = computed(() => maintenanceRequests.value.filter((request) => request.status === 'done'))
const counterDelta = computed(() => ({
  total: Math.max(Number(counterForm.counter_total || 0) - Number(machine.value?.counter_total || 0), 0),
  shift: Math.max(Number(counterForm.counter_shift || 0) - Number(machine.value?.counter_shift || 0), 0),
}))
const workflowState = computed(() => {
  if (!order.value) return 'noOrder'
  if (!assignedLot.value) return 'missingLot'
  if (machine.value?.fault_active || machine.value?.status === 'down') return 'blocked'
  if (machine.value?.status === 'setup' || !setupComplete.value) return 'setup'
  return 'ready'
})

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listMachines({ page: 1, per_page: 100 })
    machine.value = result.items.find((m) => m.id === machineId.value) ?? null
    if (!machine.value) {
      error.value = t('operator.detail.error')
      return
    }
    await Promise.all([
      loadAssignments(),
      loadDowntime(),
      loadOrderContext(),
      loadSetup(),
      loadRequirements(),
      loadFaults(),
      loadMaintenance(),
    ])
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function loadAssignments() {
  try {
    const result = await listAssignments({ machine_id: machineId.value, page: 1, per_page: 50 })
    assignments.value = result.items
  } catch {
    assignments.value = []
  }
}

async function loadSetup() {
  try {
    setupParameters.value = await getMachineSetup(machineId.value, {
      order_id: machine.value?.current_order_id ?? undefined,
      lot_number: machine.value?.current_lot_number ?? undefined,
    })
  } catch {
    setupParameters.value = []
  }
}

async function loadRequirements() {
  requirementsLoading.value = true
  requirementsError.value = ''
  try {
    requirements.value = await getMachineRequirements(machineId.value, machine.value?.current_order_id ?? undefined)
  } catch (cause) {
    requirements.value = []
    requirementsError.value = formatApiError(cause)
  } finally {
    requirementsLoading.value = false
  }
}

async function loadFaults() {
  try {
    const result = await listMachineFaults(machineId.value, { page: 1, per_page: 50 })
    faults.value = result.items
  } catch {
    faults.value = []
  }
}

async function loadMaintenance() {
  try {
    const result = await listMachineMaintenance(machineId.value, { page: 1, per_page: 50 })
    maintenanceRequests.value = result.items
  } catch {
    maintenanceRequests.value = []
  }
}

async function loadDowntime() {
  try {
    const result = await listDowntime({ machine_id: machineId.value, page: 1, per_page: 50 })
    downtimeEvents.value = result.items
  } catch {
    downtimeEvents.value = []
  }
}

async function loadDowntimeReasons() {
  try {
    downtimeReasons.value = await listDowntimeReasons()
  } catch {
    downtimeReasons.value = []
  }
}

async function loadOrderContext() {
  const orderId = machine.value?.current_order_id
  if (!orderId) return
  try {
    ;[order.value, stages.value] = await Promise.all([
      getOrder(orderId),
      listStages(orderId),
    ])
  } catch {
    order.value = null
    stages.value = []
  }
}

onMounted(() => {
  void loadDowntimeReasons()
  void load()
})

function statusLabel(status: string): string {
  if (status === 'running') return t('operator.detail.running')
  if (status === 'idle') return t('operator.detail.idle')
  if (status === 'down') return t('operator.detail.down')
  if (status === 'setup') return t('operator.detail.setup')
  return status
}

function setupLabel(code: string, fallback = ''): string {
  const localized = t(`operator.detail.setupLabels.${code}`)
  return localized && !localized.startsWith('operator.detail.setupLabels') ? localized : (fallback || code)
}

function stageName(code: string): string {
  return stages.value.find((s) => s.stage_code === code)?.stage_name ?? code
}

function openDowntimeModal() {
  downtimeForm.reason_code = downtimeReasons.value[0]?.code ?? ''
  downtimeForm.notes = ''
  actionError.value = ''
  actionMessage.value = ''
  showDowntime.value = true
}

async function submitDowntime() {
  actionError.value = ''
  if (!downtimeForm.reason_code) {
    actionError.value = t('operator.detail.selectReason')
    return
  }
  submitting.value = true
  try {
    await openDowntime({
      reason_code: downtimeForm.reason_code,
      machine_id: machineId.value,
      order_id: machine.value?.current_order_id ?? null,
      lot_number: assignedLot.value || null,
      start_time: nowApiDateTime(),
      notes: downtimeForm.notes.trim() || undefined,
    })
    showDowntime.value = false
    actionMessage.value = t('operator.detail.opened')
    await Promise.all([loadDowntime(), load()])
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function closeEvent(event: DowntimeEvent) {
  actionError.value = ''
  if (!window.confirm(t('operator.detail.closeConfirm'))) return
  submitting.value = true
  try {
    await closeDowntime(event.id, { end_time: nowApiDateTime() })
    actionMessage.value = t('operator.detail.closed')
    await Promise.all([loadDowntime(), load()])
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

function openSetup() {
  const current = new Map(setupParameters.value.map((p) => [p.code, p.value]))
  setupForm.forEach((field) => { field.value = current.get(field.code) ?? '' })
  setupNotes.value = ''
  actionError.value = ''
  actionMessage.value = ''
  showSetup.value = true
}

async function submitSetup() {
  actionError.value = ''
  if (!machine.value?.current_order_id) {
    actionError.value = t('operator.detail.noOrder')
    return
  }
  if (!assignedLot.value) {
    actionError.value = t('operator.detail.setupNeedsLot')
    return
  }
  if (setupForm.some((field) => field.value.trim() === '')) {
    actionError.value = t('operator.detail.setupRequired')
    return
  }
  submitting.value = true
  try {
    setupParameters.value = await saveMachineSetup(machineId.value, {
      order_id: machine.value.current_order_id,
      lot_number: assignedLot.value,
      parameters: setupForm
        .filter((f) => f.value.trim() !== '')
        .map((f) => ({ code: f.code, value: f.value.trim() })),
      notes: setupNotes.value.trim() || undefined,
    })
    showSetup.value = false
    actionMessage.value = t('operator.detail.saved')
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

function openFault() {
  faultForm.fault_code = ''
  faultForm.description = ''
  actionError.value = ''
  actionMessage.value = ''
  showFault.value = true
}

async function submitFault() {
  actionError.value = ''
  if (!faultForm.fault_code || !faultForm.description) {
    actionError.value = t('operator.detail.noFaults')
    return
  }
  submitting.value = true
  try {
    await reportMachineFault(machineId.value, {
      fault_code: faultForm.fault_code.trim(),
      description: faultForm.description.trim(),
      order_id: machine.value?.current_order_id ?? null,
      lot_number: assignedLot.value || null,
    })
    showFault.value = false
    actionMessage.value = t('operator.detail.faultReported')
    await Promise.all([loadFaults(), load()])
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function resolveFault(fault: MachineFault) {
  actionError.value = ''
  if (!window.confirm(t('operator.detail.resolveConfirm'))) return
  submitting.value = true
  try {
    await resolveMachineFault(machineId.value, fault.id, {})
    actionMessage.value = t('operator.detail.resolved')
    await Promise.all([loadFaults(), load()])
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

function openMaintenance() {
  maintenanceForm.reason = ''
  maintenanceForm.priority = 'medium'
  maintenanceForm.notes = ''
  actionError.value = ''
  actionMessage.value = ''
  showMaintenance.value = true
}

async function submitMaintenance() {
  actionError.value = ''
  if (!maintenanceForm.reason) {
    actionError.value = t('operator.detail.reason')
    return
  }
  submitting.value = true
  try {
    await requestMachineMaintenance(machineId.value, {
      reason: maintenanceForm.reason.trim(),
      priority: maintenanceForm.priority,
      notes: maintenanceForm.notes.trim() || undefined,
    })
    showMaintenance.value = false
    actionMessage.value = t('operator.detail.requested')
    await loadMaintenance()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

async function advanceStatus(request: MaintenanceRequest) {
  actionError.value = ''
  const next = request.status === 'open' ? 'in_progress' : 'done'
  submitting.value = true
  try {
    await updateMaintenanceStatus(request.id, { status: next })
    actionMessage.value = t('operator.detail.advance')
    await loadMaintenance()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}

function openCounter() {
  counterForm.counter_total = machine.value?.counter_total ?? 0
  counterForm.counter_shift = machine.value?.counter_shift ?? 0
  actionError.value = ''
  actionMessage.value = ''
  showCounter.value = true
}

async function submitCounter() {
  actionError.value = ''
  const total = Number(counterForm.counter_total) || 0
  const shift = Number(counterForm.counter_shift) || 0
  if (total < Number(machine.value?.counter_total || 0) || shift < Number(machine.value?.counter_shift || 0)) {
    actionError.value = t('operator.detail.counterCannotDecrease')
    return
  }
  submitting.value = true
  try {
    await recordMachineCounter(machineId.value, {
      counter_total: total,
      counter_shift: shift,
      recorded_at: nowApiDateTime(),
      client_event_id: `counter-${machineId.value}-${Date.now()}`,
    })
    showCounter.value = false
    actionMessage.value = t('operator.detail.counterRecorded')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="page-heading">
    <RouterLink class="back" to="/machines">← {{ t('operator.detail.back') }}</RouterLink>
    <div>
      <p class="eyebrow">{{ t('operator.detail.eyebrow') }}</p>
      <h2>{{ machine?.name ?? t('operator.detail.title') }}</h2>
      <p>{{ t('operator.detail.title') }}</p>
    </div>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message"><p>{{ t('operator.detail.loading') }}</p></div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('operator.detail.retry') }}</button>
  </div>

  <template v-else-if="machine">
    <!-- Status cards -->
    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-value">{{ t(`operator.detail.${machine.status}`) }}</span>
        <span class="summary-label">{{ t('operator.detail.status') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ machine.counter_shift.toLocaleString() }}</span>
        <span class="summary-label">{{ t('operator.detail.counterShift') }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-value">{{ machine.counter_total.toLocaleString() }}</span>
        <span class="summary-label">{{ t('operator.detail.counterTotal') }}</span>
      </div>
      <div class="summary-card" :class="{ attention: machine.fault_active }">
        <span class="summary-value">{{ machine.fault_active ? t('operator.machines.activeFault') : t('operator.machines.noFault') }}</span>
        <span class="summary-label">{{ t('operator.machines.fault') }}</span>
      </div>
    </div>

    <div class="detail-grid">
      <!-- Assigned order -->
      <section class="panel">
        <h3>{{ t('operator.detail.currentOrder') }}</h3>
        <p v-if="!order" class="empty-inline">{{ t('operator.detail.noOrder') }}</p>
        <template v-else>
          <p class="order-code">{{ order.mo_code }}</p>
          <p class="order-meta">{{ order.product_name }} · {{ order.department_name }}</p>
          <p class="order-meta">
            {{ Number(order.produced_quantity || 0).toLocaleString() }} / {{ order.planned_quantity.toLocaleString() }}
          </p>
          <p v-if="machine.current_lot_number" class="order-meta">
            {{ t('operator.detail.currentLot') }}: {{ machine.current_lot_number }}
          </p>
          <div class="progress-stack">
            <div class="progress-head">
              <span>{{ t('operator.detail.orderProgress') }}</span>
              <strong>{{ completionPercent }}%</strong>
            </div>
            <progress :value="completionPercent" max="100" />
            <div class="order-stats">
              <span>{{ t('operator.detail.planned') }}: <strong>{{ plannedQuantity.toLocaleString() }}</strong></span>
              <span>{{ t('operator.detail.produced') }}: <strong>{{ producedQuantity.toLocaleString() }}</strong></span>
              <span>{{ t('operator.detail.remaining') }}: <strong>{{ remainingQuantity.toLocaleString() }}</strong></span>
            </div>
          </div>
        </template>
      </section>

      <!-- Operator workflow -->
      <section class="panel workflow-panel" :class="`workflow-${workflowState}`">
        <div class="panel-header">
          <h3>{{ t('operator.detail.workflow') }}</h3>
          <span class="workflow-badge">{{ t(`operator.detail.workflowStates.${workflowState}`) }}</span>
        </div>
        <div class="workflow-grid">
          <div>
            <span>{{ t('operator.detail.assignedStage') }}</span>
            <strong>{{ currentStage ? stageName(currentStage.stage_code) : '—' }}</strong>
          </div>
          <div>
            <span>{{ t('operator.detail.assignedLot') }}</span>
            <strong>{{ assignedLot || '—' }}</strong>
          </div>
          <div>
            <span>{{ t('operator.detail.assignedOperator') }}</span>
            <strong>{{ activeAssignment?.employee_name ?? '—' }}</strong>
          </div>
          <div>
            <span>{{ t('operator.detail.machineReadiness') }}</span>
            <strong>{{ t(`operator.detail.workflowStates.${workflowState}`) }}</strong>
          </div>
          <div>
            <span>{{ t('operator.detail.setupReadiness') }}</span>
            <strong>{{ t('operator.detail.setupReadinessValue', { count: completedSetupCount, total: requiredSetupCodes.length }) }}</strong>
          </div>
          <div>
            <span>{{ t('operator.detail.materialReadiness') }}</span>
            <strong>{{ t('operator.detail.materialReadinessValue', { count: requirements.length }) }}</strong>
          </div>
        </div>
        <p class="workflow-hint">{{ t(`operator.detail.workflowHints.${workflowState}`) }}</p>
      </section>

      <!-- Assignments -->
      <section class="panel">
        <h3>{{ t('operator.detail.assignments') }}</h3>
        <div v-if="activeAssignments.length === 0" class="empty-inline">{{ t('operator.detail.noAssignments') }}</div>
        <ul v-else class="plain-list">
          <li v-for="a in activeAssignments" :key="a.id">
            <strong>{{ a.employee_name ?? a.machine_name ?? '—' }}</strong>
            <span>{{ a.stage_name ?? a.stage_code }} · {{ a.order_code }}</span>
            <span>{{ t('operator.detail.shift') }}: {{ a.shift }}</span>
          </li>
        </ul>
      </section>

      <!-- Setup parameters -->
      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('operator.detail.setupParameters') }}</h3>
          <button class="btn-secondary" :disabled="!order || !assignedLot" @click="openSetup">{{ t('operator.detail.saveSetup') }}</button>
        </div>
        <p class="setup-context">
          {{ t('operator.detail.setupContext') }}:
          <strong>{{ order?.mo_code ?? '—' }}</strong>
          <span>{{ t('operator.detail.currentLot') }}: <strong>{{ assignedLot || '—' }}</strong></span>
        </p>
        <div v-if="setupParameters.length === 0" class="empty-inline">{{ t('operator.detail.noSetup') }}</div>
        <table v-else class="data-table">
          <thead>
            <tr>
              <th>{{ t('operator.detail.setupParameters') }}</th>
              <th>{{ t('operator.detail.setupValue') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in setupParameters" :key="p.code">
              <td>{{ setupLabel(p.code, p.label) }}</td>
              <td class="cell-qty">{{ p.value }} {{ p.unit }}</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Material requirements -->
      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('operator.detail.materialRequirements') }}</h3>
          <button class="btn-secondary" :disabled="requirementsLoading || !order" @click="loadRequirements">{{ t('operator.detail.refreshMaterials') }}</button>
        </div>
        <p class="setup-context">
          {{ t('operator.detail.materialContext') }}:
          <strong>{{ order?.mo_code ?? '—' }}</strong>
          <span>{{ t('operator.detail.assignedStage') }}: <strong>{{ currentStage ? stageName(currentStage.stage_code) : '—' }}</strong></span>
          <span>{{ t('operator.detail.plannedPairs') }}: <strong>{{ (plannedMaterialPairs || plannedQuantity).toLocaleString() }}</strong></span>
        </p>
        <div v-if="requirementsLoading" class="empty-inline">{{ t('operator.detail.materialsLoading') }}</div>
        <div v-else-if="requirementsError" class="inline-error">
          <span>{{ requirementsError }}</span>
          <button class="link-action" @click="loadRequirements">{{ t('operator.detail.retry') }}</button>
        </div>
        <div v-else-if="requirements.length === 0" class="empty-inline">{{ t('operator.detail.noRequirements') }}</div>
        <div v-if="!requirementsLoading && !requirementsError && materialTotals.length" class="material-totals">
          <span v-for="total in materialTotals" :key="total.unit || 'unitless'">
            {{ t('operator.detail.totalRequired') }}:
            <strong>{{ total.quantity.toLocaleString() }} {{ total.unit }}</strong>
          </span>
        </div>
        <table v-if="!requirementsLoading && !requirementsError && currentStageRequirements.length" class="data-table material-table">
          <thead>
            <tr>
              <th>{{ t('operator.detail.material') }}</th>
              <th>{{ t('operator.detail.stage') }}</th>
              <th>{{ t('operator.detail.requiredQuantity') }}</th>
              <th>{{ t('operator.detail.requiredQuantity') }} ({{ t('operator.detail.perPair') }})</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, index) in currentStageRequirements" :key="`${r.material_id}-${index}`">
              <td>{{ r.material_name }}</td>
              <td>{{ r.stage_name }}</td>
              <td class="cell-qty">{{ r.required_quantity.toLocaleString() }} {{ r.unit }}</td>
              <td class="cell-qty">{{ r.per_pair }} {{ r.unit }}</td>
            </tr>
          </tbody>
        </table>
        <p v-if="!requirementsLoading && !requirementsError && requirements.length && currentStageRequirements.length === 0" class="empty-inline">
          {{ t('operator.detail.noStageRequirements') }}
        </p>
      </section>

      <!-- Downtime -->
      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('operator.detail.downtime') }}</h3>
          <button class="btn-secondary" :disabled="openDowntimeEvents.length > 0" @click="openDowntimeModal">{{ t('operator.detail.reportDowntime') }}</button>
        </div>
        <div class="mini-stats">
          <span>{{ t('operator.detail.openItems', { count: openDowntimeEvents.length }) }}</span>
          <span>{{ t('operator.detail.closedItems', { count: closedDowntimeEvents.length }) }}</span>
        </div>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
        <div v-if="downtimeEvents.length === 0" class="empty-inline">{{ t('operator.detail.noDowntime') }}</div>
        <table v-else class="data-table">
          <thead>
            <tr>
              <th>{{ t('operator.detail.currentOrder') }}</th>
              <th>{{ t('operator.detail.status') }}</th>
              <th>{{ t('operator.detail.employee') }}</th>
              <th>{{ t('operator.detail.stage') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="e in [...openDowntimeEvents, ...closedDowntimeEvents]" :key="e.id">
              <td>{{ e.reason_name }}</td>
              <td>{{ e.end_time ? t('operator.detail.closedStatus') : t('operator.detail.openStatus') }}</td>
              <td>{{ e.reported_by }}</td>
              <td>{{ formatDisplayDateTime(e.start_time) || '—' }}</td>
              <td>
                <button v-if="!e.end_time" class="link-action danger" @click="closeEvent(e)">{{ t('operator.detail.close') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Faults -->
      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('operator.detail.faults') }}</h3>
          <button class="btn-secondary" @click="openFault">{{ t('operator.detail.reportFault') }}</button>
        </div>
        <div class="mini-stats">
          <span>{{ t('operator.detail.openItems', { count: openFaults.length }) }}</span>
          <span>{{ t('operator.detail.resolvedItems', { count: resolvedFaults.length }) }}</span>
        </div>
        <div v-if="faults.length === 0" class="empty-inline">{{ t('operator.detail.noFaults') }}</div>
        <table v-else class="data-table">
          <thead>
            <tr>
              <th>{{ t('operator.detail.description') }}</th>
              <th>{{ t('operator.detail.status') }}</th>
              <th>{{ t('operator.detail.employee') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in [...openFaults, ...resolvedFaults]" :key="f.id">
              <td>{{ f.description }}</td>
              <td>
                <span :class="['status-badge', f.status === 'open' ? 'status-open' : 'status-resolved']">{{ f.status === 'open' ? t('operator.detail.openStatus') : t('operator.detail.resolvedStatus') }}</span>
              </td>
              <td>{{ f.reported_by }}</td>
              <td>
                <button v-if="f.status === 'open'" class="link-action" @click="resolveFault(f)">{{ t('operator.detail.resolve') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- Maintenance -->
      <section class="panel">
        <div class="panel-header">
          <h3>{{ t('operator.detail.maintenance') }}</h3>
          <button class="btn-secondary" @click="openMaintenance">{{ t('operator.detail.requestMaintenance') }}</button>
        </div>
        <div class="mini-stats">
          <span>{{ t('operator.detail.openItems', { count: openMaintenanceRequests.length }) }}</span>
          <span>{{ t('operator.detail.doneItems', { count: doneMaintenanceRequests.length }) }}</span>
        </div>
        <div v-if="maintenanceRequests.length === 0" class="empty-inline">{{ t('operator.detail.noMaintenance') }}</div>
        <table v-else class="data-table">
          <thead>
            <tr>
              <th>{{ t('operator.detail.reason') }}</th>
              <th>{{ t('operator.detail.priority') }}</th>
              <th>{{ t('operator.detail.status') }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="m in [...openMaintenanceRequests, ...doneMaintenanceRequests]" :key="m.id">
              <td>{{ m.reason }}</td>
              <td>{{ t(`operator.detail.priority${m.priority.charAt(0).toUpperCase()}${m.priority.slice(1)}`) }}</td>
              <td>{{ m.status === 'in_progress' ? t('operator.detail.statusInProgress') : m.status === 'done' ? t('operator.detail.statusDone') : t('operator.detail.openStatus') }}</td>
              <td>
                <button v-if="m.status !== 'done'" class="link-action" @click="advanceStatus(m)">{{ t('operator.detail.advance') }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

    <!-- Counter record button -->
    <div class="counter-bar">
      <span>{{ t('operator.detail.counterHint', { total: machine.counter_total.toLocaleString(), shift: machine.counter_shift.toLocaleString() }) }}</span>
      <button class="btn-primary" @click="openCounter">{{ t('operator.detail.recordCounter') }}</button>
    </div>

    <!-- Downtime modal -->
    <div v-if="showDowntime" class="modal-overlay" @click.self="showDowntime = false">
      <form class="modal" @submit.prevent="submitDowntime">
        <h3>{{ t('operator.detail.reportDowntime') }}</h3>
        <p class="modal-sub">{{ order?.mo_code ?? '—' }} · {{ assignedLot || '—' }}</p>
        <label>{{ t('operator.detail.selectReason') }}
          <select v-model="downtimeForm.reason_code" required>
            <option v-for="r in downtimeReasons" :key="r.code" :value="r.code">{{ r.name }}</option>
          </select>
        </label>
        <label>{{ t('operator.detail.notes') }}
          <input v-model="downtimeForm.notes" type="text" />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showDowntime = false">{{ t('operator.detail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('operator.detail.reporting') : t('operator.detail.report') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Setup modal -->
    <div v-if="showSetup" class="modal-overlay" @click.self="showSetup = false">
      <form class="modal" @submit.prevent="submitSetup">
        <h3>{{ t('operator.detail.saveSetup') }}</h3>
        <p class="modal-sub">{{ order?.mo_code ?? '—' }} · {{ assignedLot || '—' }}</p>
        <div v-for="field in setupForm" :key="field.code" class="setup-row">
          <label>{{ setupLabel(field.code) }}</label>
          <input v-model.trim="field.value" type="text" required />
        </div>
        <label>{{ t('operator.detail.notes') }}
          <input v-model="setupNotes" type="text" :placeholder="t('operator.detail.setupNotesPlaceholder')" />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showSetup = false">{{ t('operator.detail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('operator.detail.saving') : t('operator.detail.save') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Fault modal -->
    <div v-if="showFault" class="modal-overlay" @click.self="showFault = false">
      <form class="modal" @submit.prevent="submitFault">
        <h3>{{ t('operator.detail.reportFault') }}</h3>
        <label>{{ t('operator.detail.faultCode') }}
          <input v-model="faultForm.fault_code" type="text" required />
        </label>
        <label>{{ t('operator.detail.description') }}
          <input v-model="faultForm.description" type="text" :placeholder="t('operator.detail.reportFaultPlaceholder')" required />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showFault = false">{{ t('operator.detail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('operator.detail.saving') : t('operator.detail.report') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Maintenance modal -->
    <div v-if="showMaintenance" class="modal-overlay" @click.self="showMaintenance = false">
      <form class="modal" @submit.prevent="submitMaintenance">
        <h3>{{ t('operator.detail.requestMaintenance') }}</h3>
        <label>{{ t('operator.detail.reason') }}
          <input v-model="maintenanceForm.reason" type="text" required />
        </label>
        <label>{{ t('operator.detail.priority') }}
          <select v-model="maintenanceForm.priority">
            <option value="low">{{ t('operator.detail.priorityLow') }}</option>
            <option value="medium">{{ t('operator.detail.priorityMedium') }}</option>
            <option value="high">{{ t('operator.detail.priorityHigh') }}</option>
          </select>
        </label>
        <label>{{ t('operator.detail.notes') }}
          <input v-model="maintenanceForm.notes" type="text" />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showMaintenance = false">{{ t('operator.detail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('operator.detail.saving') : t('operator.detail.requestMaintenance') }}
          </button>
        </div>
      </form>
    </div>

    <!-- Counter modal -->
    <div v-if="showCounter" class="modal-overlay" @click.self="showCounter = false">
      <form class="modal" @submit.prevent="submitCounter">
        <h3>{{ t('operator.detail.recordCounter') }}</h3>
        <p class="modal-sub">{{ t('operator.detail.counterDelta', { total: counterDelta.total.toLocaleString(), shift: counterDelta.shift.toLocaleString() }) }}</p>
        <label>{{ t('operator.detail.counterTotalLabel') }}
          <input v-model.number="counterForm.counter_total" type="number" min="0" required />
        </label>
        <label>{{ t('operator.detail.counterShiftLabel') }}
          <input v-model.number="counterForm.counter_shift" type="number" min="0" required />
        </label>
        <small v-if="actionError" class="error">{{ actionError }}</small>
        <div class="modal-actions">
          <button type="button" class="btn-secondary" @click="showCounter = false">{{ t('operator.detail.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="submitting">
            {{ submitting ? t('operator.detail.recordingCounter') : t('operator.detail.recordCounter') }}
          </button>
        </div>
      </form>
    </div>
  </template>
</template>

<style scoped>
.page-heading { margin-bottom: 20px; }
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }
.back {
  display: inline-block;
  margin-bottom: 12px;
  color: #147a8a;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
}
.back:hover { text-decoration: underline; }

.state-message { padding: 40px 0; text-align: center; color: #5f6b6a; }
.state-message.error { color: #b33a3a; }
.empty-inline { color: #8b9a95; font-size: 13px; font-style: italic; }
.error { color: #b33a3a; font-size: 12px; }
.inline-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #efb5ad;
  border-radius: 8px;
  background: #fff3f1;
  color: #852d22;
  font-size: 13px;
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}
.summary-card {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  padding: 16px 20px;
  text-align: center;
}
.summary-card.attention { border-color: #f0c040; background: #fffdf5; }
.summary-value { display: block; font-size: 22px; font-weight: 800; color: #1a2b26; }
.summary-label { display: block; font-size: 11px; color: #65736e; text-transform: uppercase; font-weight: 600; margin-top: 2px; }

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.panel {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 12px;
  padding: 20px;
}
.panel h3 { margin: 0 0 12px; font-size: 15px; font-weight: 700; color: #1a2b26; }
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.panel-header h3 { margin: 0; }

.order-code { font-size: 16px; font-weight: 800; color: #1a2b26; font-family: monospace; margin: 0; }
.order-meta { margin: 4px 0 0; font-size: 13px; color: #65736e; }
.progress-stack { display: grid; gap: 8px; margin-top: 14px; }
.progress-head, .order-stats {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  color: #65736e;
  font-size: 12px;
}
.progress-head strong, .order-stats strong { color: #1a2b26; }
.progress-stack progress {
  width: 100%;
  height: 10px;
  overflow: hidden;
  border: 0;
  border-radius: 999px;
  background: #edf2f0;
}
.progress-stack progress::-webkit-progress-bar { background: #edf2f0; }
.progress-stack progress::-webkit-progress-value { background: #147a8a; }
.progress-stack progress::-moz-progress-bar { background: #147a8a; }

.workflow-panel { border-left: 4px solid #147a8a; }
.workflow-blocked { border-left-color: #b33a3a; background: #fffafa; }
.workflow-missingLot, .workflow-setup { border-left-color: #d49a22; background: #fffdf5; }
.workflow-noOrder { border-left-color: #8b9a95; }
.workflow-badge {
  flex: 0 0 auto;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eaf0ed;
  color: #3d5a4f;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
}
.workflow-ready .workflow-badge { background: #dcefe7; color: #1f5c4b; }
.workflow-blocked .workflow-badge { background: #fde8e8; color: #b33a3a; }
.workflow-missingLot .workflow-badge, .workflow-setup .workflow-badge { background: #fef3cd; color: #856404; }
.workflow-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.workflow-grid div {
  display: grid;
  gap: 3px;
  padding: 10px;
  border: 1px solid #eef2f0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.7);
}
.workflow-grid span { color: #65736e; font-size: 11px; font-weight: 700; text-transform: uppercase; }
.workflow-grid strong { color: #1a2b26; font-size: 13px; }
.workflow-hint { margin: 12px 0 0; color: #53625d; font-size: 13px; line-height: 1.5; }

.plain-list { list-style: none; margin: 0; padding: 0; }
.plain-list li {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f2f1;
  font-size: 13px;
  color: #1a2b26;
}
.plain-list li:last-child { border-bottom: none; }
.plain-list span { color: #65736e; font-size: 12px; }

.placeholder-note {
  color: #8b9a95;
  font-size: 12px;
  font-style: italic;
  margin: 0;
  background: #f6f8f7;
  border: 1px dashed #e2e6e3;
  border-radius: 8px;
  padding: 12px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}
.data-table th {
  text-align: left;
  padding: 10px 12px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #65736e;
  background: #f6f8f7;
  border-bottom: 1px solid #e2e6e3;
}
.data-table td {
  padding: 10px 12px;
  font-size: 13px;
  color: #1a2b26;
  border-bottom: 1px solid #f0f2f1;
}
.data-table tr:last-child td { border-bottom: none; }

.action-message {
  padding: 10px 14px;
  background: #dcefe7;
  color: #1f5c4b;
  border-radius: 8px;
  font-size: 13px;
  margin: 8px 0;
}

.link-action {
  color: #147a8a;
  font-weight: 600;
  font-size: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.link-action:hover { text-decoration: underline; }
.link-action.danger { color: #b33a3a; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 20, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.modal {
  width: 380px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.modal h3 { margin: 0; font-size: 16px; font-weight: 700; }
.modal label {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 600;
  color: #65736e;
  gap: 4px;
}
.modal input, .modal select {
  padding: 10px 12px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.btn-primary {
  display: inline-block;
  padding: 8px 20px;
  background: #147a8a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.btn-primary:disabled { opacity: 0.5; cursor: default; }
.btn-primary:hover:not(:disabled) { background: #0f6573; }
.btn-secondary {
  padding: 8px 16px;
  background: #fff;
  color: #147a8a;
  border: 1px solid #147a8a;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.mini-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 12px;
}
.mini-stats span {
  padding: 5px 9px;
  border-radius: 999px;
  background: #f1f5f3;
  color: #53625d;
  font-size: 12px;
  font-weight: 700;
}
.btn-secondary:hover { background: #f0f7f8; }
.btn-secondary:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-secondary:disabled:hover { background: #fff; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: #eaf0ed;
  color: #3d5a4f;
}
.status-open { background: #fde8e8; color: #b33a3a; }
.status-resolved { background: #dcefe7; color: #1f5c4b; }

.counter-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
}
.counter-bar span { color: #65736e; font-size: 13px; }

.setup-row {
  display: flex;
  gap: 12px;
  align-items: center;
}
.setup-row label {
  width: 120px;
  font-size: 12px;
  font-weight: 700;
  color: #65736e;
  text-transform: uppercase;
  margin: 0;
}
.setup-row input {
  flex: 1;
}
.setup-context, .modal-sub {
  margin: 0 0 12px;
  color: #65736e;
  font-size: 12px;
}
.setup-context {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.setup-context strong, .modal-sub strong { color: #1a2b26; }
.material-totals {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0 12px;
}
.material-totals span {
  padding: 7px 10px;
  border: 1px solid #d7e6df;
  border-radius: 8px;
  background: #f6fbf8;
  color: #53625d;
  font-size: 12px;
}
.material-totals strong { color: #1f5c4b; }
.material-table { margin-top: 10px; }

@media (max-width: 900px) {
  .summary-row { grid-template-columns: repeat(2, 1fr); }
  .detail-grid { grid-template-columns: 1fr; }
  .order-stats { flex-wrap: wrap; }
  .workflow-grid { grid-template-columns: 1fr; }
}
</style>
